import GoogleMapReact from 'google-map-react'
import React, { useEffect, useRef, useState } from 'react'
import { google as googleT } from 'google-maps'
import useSupercluster from 'use-supercluster'
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import PlaceIcon from '@mui/icons-material/Place'
import { isBefore } from 'date-fns'
import { haversineCalculate } from '../../utils/helpers'
import { ENV } from '../../constants/general'
import LocationCardMap from '../../components/LocationCard/Map'
import { GOOGLE_MAP_STYLERS } from '../../constants/googleMapStylers'
import { ListContainer, MainContainer, MapLegend } from './styles'
import ZipcodeFilter from '../../components/ZipcodeFilter'
import LocationCardList from '../../components/LocationCard/List'
import {
  LocationList,
  LocationListItem,
  useFetchLocationList,
} from '../../utils/hooks'
import ClusterMarker from '../../components/ClusterMarker'

declare const google: googleT

type ClusterType = {
  properties: LocationListItem & {
    cluster: boolean
    point_count: number
  }
  geometry: { coordinates: number[] }
  id: number
}

const LA_JOLLA_LATLNG = [32.8832243, -117.2265539]

const sortByDistance = (a: LocationListItem, b: LocationListItem) =>
  (a.distance || 0) > (b.distance || 0) ? 1 : -1

const sortByNextSlot = (a: LocationListItem, b: LocationListItem) => {
  if (!a.nextOfficeSlot && !b.nextOfficeSlot) {
    return a.name.localeCompare(b.name)
  }
  if (!a.nextOfficeSlot) {
    return 1
  }
  if (!b.nextOfficeSlot) {
    return -1
  }
  return isBefore(a.nextOfficeSlot, b.nextOfficeSlot) ? -1 : 1
}

const LocationsList = (): JSX.Element => {
  const [gettingLocation, setGettingLocation] = useState<boolean>(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(1060))
  const { data, error, isLoading } = useFetchLocationList()
  const [selected, setSelected] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<string>('zipcode')
  const [latlng, setLatlng] = useState<Array<number>>()
  const [zoomLoaded, setZoomLoaded] = useState(false)
  const firstLoad = useRef(true)
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>()
  const [gmap, setGmap] = useState<google.maps.Map>()
  const [bounds, setBounds] = useState<string[] | null>(null)
  const [sortedLocations, setSortedLocations] = useState<LocationList>([])
  const supercluster = useSupercluster({
    points: sortedLocations.map((loc) => ({
      type: 'Feature',
      properties: { cluster: false, ...loc },
      geometry: {
        type: 'Point',
        coordinates: [loc.longitude, loc.latitude],
      },
    })),
    bounds,
    zoom: gmap?.getZoom() || 10,
    options: { radius: 75, maxZoom: 15 },
  })

  useEffect(() => {
    // the purpose of this useEffect is to set the zoom level property to prevent
    // clustering of each location. Only meant to calculate it once on first load.
    if (!zoomLoaded && supercluster.clusters.length) {
      setSortedLocations(
        sortedLocations.map((loc) => {
          // if location is not in a cluster, but as an individual point
          if (supercluster.clusters.find((cl) => cl.properties.id === loc.id))
            return { ...loc, zoom: 11 }

          // if location is in a cluster (could be any step of the cluster tree)
          const isInCluster = supercluster.clusters
            .filter((cl) => cl.properties.cluster)
            .find((cl) =>
              supercluster.supercluster
                .getLeaves(cl.id)
                .find((scl: any) => scl.properties.id === loc.id)
            )

          // if location is in a cluster as a direct child
          const directChildCluster = supercluster.clusters
            .filter((cl) => cl.properties.cluster)
            .find((cl) =>
              supercluster.supercluster
                .getChildren(cl.id)
                .find((scl: any) => scl.properties.id === loc.id)
            )

          if (isInCluster)
            if (directChildCluster)
              return {
                ...loc,
                zoom: supercluster.supercluster.getClusterExpansionZoom(
                  directChildCluster.id
                ),
              }
            else return { ...loc, zoom: 16 }

          return { ...loc, zoom: 11 }
        })
      )
      setZoomLoaded(true)
    }
  }, [supercluster.clusters])

  useEffect(() => {
    if (latlng?.length && latlng[0] && latlng[1]) {
      const withDistance = (sortedLocations.length ? sortedLocations : data)
        .map((loc) => ({
          ...loc,
          distance:
            loc.latitude && loc.longitude && latlng.length > 1
              ? haversineCalculate(
                  loc.latitude,
                  loc.longitude,
                  latlng[0],
                  latlng[1]
                )
              : null,
        }))
        .sort(sortBy === 'zipcode' ? sortByDistance : sortByNextSlot)
      setSortedLocations(withDistance)
      const listDiv = document.getElementsByClassName('content')
      if (listDiv.length && typeof listDiv[0].scroll === 'function') {
        listDiv[0].scroll({ top: 0, behavior: 'smooth' })
      }
      if (gmap && withDistance.length) {
        gmap.setZoom(11)
        gmap.panTo({
          lat: withDistance[0].latitude,
          lng: isMobile
            ? withDistance[0].longitude
            : withDistance[0].longitude - 0.175,
        })
      }
    } else {
      if (firstLoad.current) {
        setSortBy('nextSlot')
        firstLoad.current = false
      }
      setSortedLocations(
        (sortedLocations.length ? sortedLocations : data).sort(sortByNextSlot)
      )
      if (gmap) {
        gmap.setZoom(isMobile ? 9 : 10)
        gmap.panTo({
          lat: LA_JOLLA_LATLNG[0],
          lng: isMobile ? LA_JOLLA_LATLNG[1] : LA_JOLLA_LATLNG[1] - 0.175,
        })
      }
    }
  }, [latlng, data, sortBy])

  const handleApiLoaded = (m: google.maps.Map, ms: google.maps.Geocoder) => {
    setGeocoder(ms)
    setGmap(m)
  }

  const createMapOptions = (): google.maps.MapOptions => ({
    styles: GOOGLE_MAP_STYLERS,
  })

  // calculation to make shift of x-axis on the map exponentially lower, the higher the zoom
  const calculateXShift = (zoom: number): number => 0.175 / 2 ** (zoom - 11)

  const onClick = (
    id: string,
    lat?: number,
    lng?: number,
    isCluster?: boolean,
    zoomLevel?: number
  ) => {
    if (isCluster && gmap && lat && lng) {
      const newZoom = supercluster.supercluster.getClusterExpansionZoom(
        Number(id)
      )

      gmap.setZoom(newZoom)
      gmap.panTo({
        lat,
        lng: isMobile ? lng : lng - calculateXShift(newZoom),
      })
      return
    }
    if (selected === id) {
      setSelected(null)
    } else {
      setSelected(id)
      if (gmap && lat && lng) {
        gmap.setZoom(zoomLevel || 11)
        gmap.panTo({
          lat,
          lng: isMobile ? lng : lng - calculateXShift(zoomLevel || 11),
        })
      }
    }
  }

  return (
    <MainContainer>
      <div>
        <GoogleMapReact
          bootstrapURLKeys={{ key: ENV.GOOGLE_MAPS_API_KEY }}
          defaultCenter={{
            lat: LA_JOLLA_LATLNG[0],
            lng: isMobile ? LA_JOLLA_LATLNG[1] : LA_JOLLA_LATLNG[1] - 0.175,
          }}
          options={createMapOptions}
          defaultZoom={isMobile ? 9 : 10}
          yesIWantToUseGoogleMapApiInternals
          onChange={({ bounds: bnds }: { bounds: any }) => {
            setBounds([bnds.nw.lng, bnds.se.lat, bnds.se.lng, bnds.nw.lat])
          }}
          onGoogleApiLoaded={({
            map,
            maps,
          }: {
            map: google.maps.Map
            maps: { Geocoder: any }
            // Geocoder object being hard to assign an actual type and not breaking in some other places.
          }) => handleApiLoaded(map, new maps.Geocoder())}
        >
          {supercluster.clusters.map((cl: ClusterType) =>
            cl.properties.cluster ? (
              <ClusterMarker
                key={cl.id}
                // @ts-expect-error: google-map-react autohandling lat and lng prop and ts not realizing it
                lat={cl.geometry.coordinates[1] || 0}
                lng={cl.geometry.coordinates[0] || 0}
                locationCount={cl.properties.point_count}
                onClick={() =>
                  onClick(
                    cl.id.toString(),
                    cl.geometry.coordinates[1] || 0,
                    cl.geometry.coordinates[0] || 0,
                    true
                  )
                }
              />
            ) : (
              <LocationCardMap
                key={cl.id}
                id={cl.properties.id}
                isPediatrics={cl.properties.isPediatrics}
                name={cl.properties.name}
                image={cl.properties.image}
                // @ts-expect-error: google-map-react autohandling lat and lng prop and ts not realizing it
                lat={cl.properties.latitude}
                lng={cl.properties.longitude}
                address={cl.properties.address}
                city={cl.properties.city}
                zip={cl.properties.zip}
                phoneNumber={cl.properties.phone}
                distance={cl.properties.distance}
                videoSlot={cl.properties.nextVideoSlot}
                officeSlot={cl.properties.nextOfficeSlot}
                onToggle={() =>
                  onClick(
                    cl.properties.id,
                    cl.properties.latitude,
                    cl.properties.longitude,
                    false,
                    cl.properties.zoom
                  )
                }
                selected={cl.properties.id === selected}
                locationUrl={cl.properties.locationUrl}
              />
            )
          )}
        </GoogleMapReact>
      </div>
      {gettingLocation && (
        <>
          <Box sx={{zIndex: 9, background: '#000', opacity: 0.5, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}/>
          <Paper elevation={5} sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '8px', background: '#FFF', padding: '16px', width: '230px', zIndex: 10 }}>
            <Typography fontSize={24} fontWeight={500} color="primary">
              Getting your location...
            </Typography>
          </Paper>
        </>
      )}
      <ListContainer>
        <ZipcodeFilter
          onSearch={(lat, lng) => setLatlng([lat, lng])}
          geocoder={geocoder}
          sortBy={sortBy}
          handleSortBy={setSortBy}
          disableSortByZipcode={!(latlng?.length && latlng[0] && latlng[1])}
          setGettingLocation={setGettingLocation}
        />
        {!isLoading && sortedLocations.length ? (
          <div className="content">
            {sortedLocations.map((loc) => (
              <React.Fragment key={loc.id}>
                <LocationCardList
                  id={loc.id}
                  name={loc.name}
                  isPediatrics={loc.isPediatrics}
                  image={loc.image}
                  // @ts-expect-error: google-map-react autohandling lat and lng prop and ts not realizing it
                  lat={loc.latitude}
                  lng={loc.longitude}
                  address={loc.address}
                  city={loc.city}
                  zip={loc.zip}
                  phoneNumber={loc.phone}
                  distance={loc.distance}
                  videoSlot={loc.nextVideoSlot}
                  officeSlot={loc.nextOfficeSlot}
                  locationUrl={loc.locationUrl}
                  onClick={() =>
                    onClick(
                      loc.id,
                      loc.latitude,
                      loc.longitude,
                      false,
                      loc.zoom
                    )
                  }
                />
                <Divider />
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="loader">
            <CircularProgress />
          </div>
        )}
      </ListContainer>
      <MapLegend>
        <div>
          <PlaceIcon />
          <Typography>Perlman Clinic</Typography>
        </div>
        <div>
          <PlaceIcon />
          <Typography>Perlman Pediatrics</Typography>
        </div>
      </MapLegend>
    </MainContainer>
  )
}

export default LocationsList
